import { cn, Icon, type IconName, Select, SelectContent, SelectItem, SelectTrigger, Text } from '@mntn/ui-components'
import { useMemo } from 'react'
import { SLATE_THEME_CLASSNAME } from '@/components/shared/mfe-theme-wrapper'
import type { SortState } from '../dashboard'

type SortOption = {
  key: string
  label: string
  value: SortState
  iconName?: IconName
}

const SORT_OPTIONS: SortOption[] = [
  {
    key: 'search',
    label: 'Relevance',
    value: { order: 'desc', orderBy: 'search' },
    iconName: 'ArrowUpDown',
  },
  {
    key: 'created_desc',
    label: 'Created (Newest First)',
    value: { order: 'desc', orderBy: 'created' },
    iconName: 'ArrowUpWideNarrow',
  },
  {
    key: 'created_asc',
    label: 'Created (Oldest First)',
    value: { order: 'asc', orderBy: 'created' },
    iconName: 'ArrowDownWideNarrow',
  },
  {
    key: 'name_asc',
    label: 'Name (A → Z)',
    value: { order: 'asc', orderBy: 'name' },
    iconName: 'ArrowDownAZ',
  },
  {
    key: 'name_desc',
    label: 'Name (Z → A)',
    value: { order: 'desc', orderBy: 'name' },
    iconName: 'ArrowUpAZ',
  },
  {
    key: 'expiration_asc',
    label: 'Expiration (Soonest First)',
    value: { order: 'asc', orderBy: 'expiration' },
    iconName: 'ArrowDownWideNarrow',
  },
  {
    key: 'expiration_desc',
    label: 'Expiration (Latest First)',
    value: { order: 'desc', orderBy: 'expiration' },
    iconName: 'ArrowUpWideNarrow',
  },
] as const

type SortOptionKey = (typeof SORT_OPTIONS)[number]['key']

const SORT_MAP: Record<SortOptionKey, SortState> = Object.fromEntries(SORT_OPTIONS.map(opt => [opt.key, opt.value]))

const getSortKey = (state: SortState) =>
  SORT_OPTIONS.find(opt => opt.value.order === state.order && opt.value.orderBy === state.orderBy)?.key

interface SortFieldDropdownProps {
  sortState: SortState
  setSortState: (sortState: SortState) => void
  search: string
  className?: string
}

// biome-ignore lint/plugin: RETIRED, do not copy. Convert to a named function export when touching this.
export const SortFieldDropdown: React.FC<SortFieldDropdownProps> = ({
  sortState,
  setSortState,
  search = '',
  className,
}) => {
  const selected = getSortKey(sortState)

  const handleChange = (selected: SortOptionKey) => {
    const newState = SORT_MAP[selected]
    if (newState) setSortState(newState)
  }

  const visibleOptions = useMemo(
    () => SORT_OPTIONS.filter(option => (option.key === 'search' ? !!search : true)),
    [search],
  )

  const selectedOption = useMemo(() => SORT_OPTIONS.find(opt => opt.key === selected), [selected])

  return (
    <div className={cn('flex-shrink-0', className)}>
      <Select onValueChange={handleChange} value={selected}>
        <SelectTrigger
          className="rounded-xs border-0 flex items-center justify-between px-6 py-4 text-header-inverse bg-sub-surface-bg-inverse"
          iconRight={<Icon name="ChevronDown" className="text-inherit" />}
        >
          <div className="flex gap-1 items-center">
            <Icon name={selectedOption?.iconName ?? 'ArrowUpDown'} className="text-inherit size-5" />
            <Text className="truncate align-middle text-inherit" variant="base-sb">
              {selectedOption?.label}
            </Text>
          </div>
        </SelectTrigger>

        {/* note: this component is rendered at the end of the document body so we need to pass in the prefix and theme classes to get it properly styled */}
        <SelectContent
          wrapperClassName={SLATE_THEME_CLASSNAME}
          className="bg-sub-surface-bg-inverse border-0 rounded p-4 -mt-2"
          align="end"
        >
          {visibleOptions.map(option => (
            <SelectItem
              persistIcon
              key={option.key}
              value={option.key}
              iconName={option.iconName}
              className={
                'rounded-xs text-secondary focus:text-header-inverse bg-inherit focus:bg-surface-bg-inverse pr-0'
              }
            >
              <Text className="truncate text-inherit" variant="base-sb">
                {option.label}
              </Text>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}
